import React, { Suspense, useEffect, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { notification } from 'antd';

import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { useFilterStore } from '@/entities/discovery';
import { useSearchStore } from '@/entities/search';

import { fetchDomainAggregations } from '@/features/dataSearch/api/domainAggregationApi';
import { searchOpenMetadata } from '@/features/dataSearch/api/openmetadataSearchApi';
import { updateSearchURL } from '@/features/dataSearch/lib';
import { useSearchUrlSync } from '@/features/search/model';

import { AssetTree } from '@/widgets/discovery/AssetTree';
import { TopFilterBar } from '@/widgets/discovery/TopFilterBar';
import { SearchResultsContainer } from '@/widgets/search';

import styles from './SearchPage.module.css';

export const SearchPage: React.FC = () => {
  const [topFilterBarKey, setTopFilterBarKey] = useState(0);
  const {
    query,
    setFilters: setSearchFilters,
    isInitialAggregationsLoaded,
    setInitialAggregations,
    setDomainAggregations,
  } = useSearchStore();
  const filters = useFilterStore((state) => state.filters);

  const { isReady } = useSearchUrlSync();

  useEffect(() => {
    const loadInitialAggregations = async () => {
      if (!isInitialAggregationsLoaded) {
        try {
          const emptyFilters = {
            dataSource: [],
            assetType: [],
            domain: [],
            owner: [],
            tier: [],
            tags: [],
            certification: [],
          };

          const [searchResponse, domainResponse] = await Promise.all([
            searchOpenMetadata('', emptyFilters, 0, 0),
            fetchDomainAggregations(),
          ]);

          if (searchResponse.aggregations) {
            setInitialAggregations(searchResponse.aggregations);
          }

          if (domainResponse.aggregations) {
            const domainBuckets = domainResponse.aggregations['sterms#domains.name.keyword']?.buckets || [];
            setDomainAggregations(domainBuckets);
          }
        } catch (error) {
          console.error('Failed to load initial aggregations:', error);
          notification.error({
            message: '필터 옵션 로드 실패',
            description: '필터 옵션을 불러오지 못했습니다.',
            duration: 5,
            placement: 'bottomLeft',
          });
        }
      }
    };

    loadInitialAggregations();
  }, [isInitialAggregationsLoaded, setInitialAggregations, setDomainAggregations]);

  useEffect(() => {
    return () => {
      useFilterStore.getState().clearAllFilters();
      useSearchStore.getState().setQuery('');
    };
  }, []);

  useEffect(() => {
    if (isReady) {
      setSearchFilters(filters);
      updateSearchURL(query, filters);
    }
  }, [query, filters, setSearchFilters, isReady]);

  return (
    <div className={styles.searchPage}>
      <div className={styles.treeSection}>
        <ErrorBoundary
          FallbackComponent={(props) => (
            <div style={{ padding: '16px' }}>
              <ErrorFallback {...props} description="데이터 자산 트리를 불러오는 중 문제가 발생했습니다." />
            </div>
          )}>
          <AssetTree />
        </ErrorBoundary>
      </div>

      <div className={styles.contentSection}>
        <ErrorBoundary
          FallbackComponent={(props) => (
            <ErrorFallback
              {...props}
              variant="compact"
              height={32}
              description="필터 바를 불러오는 중 문제가 발생했습니다."
            />
          )}
          onReset={() => setTopFilterBarKey((prev) => prev + 1)}
          resetKeys={[topFilterBarKey]}>
          <TopFilterBar key={topFilterBarKey} />
        </ErrorBoundary>

        <ErrorBoundary
          FallbackComponent={(props) => (
            <div style={{ padding: '16px' }}>
              <ErrorFallback {...props} description="검색 결과를 불러오는 중 문제가 발생했습니다." />
            </div>
          )}>
          {isReady ? (
            <Suspense fallback={<LoadingState message="검색 결과를 불러오는 중..." />}>
              <SearchResultsContainer />
            </Suspense>
          ) : (
            <LoadingState message="검색 결과를 불러오는 중..." />
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
};
